/*
 * Copyright (c) 2023-2024 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import { Popover } from 'antd'
import { Fragment, ReactNode } from 'react'

import { CheckCircleFilled, CloseCircleFilled } from '@ant-design/icons'

function RequirementItem({ isValid, description }: { isValid: boolean; description: Requirement['description'] }) {
  // Using "absolute top-1 left-0" in the className to position the icon similarly to
  // the default list icon from the HTML list tag (<li>).
  let icon = <CloseCircleFilled className="absolute left-0 top-1 [&>svg]:fill-error-base" aria-label="Doesn't meet requirement" />
  if (isValid) icon = <CheckCircleFilled className="absolute left-0 top-1 [&>svg]:fill-success-base" aria-label="Meets requirement" />

  return (
    <li className="relative pl-6">
      {icon}
      {description}
    </li>
  )
}

type ValidatorFunction = (value: string) => boolean

type ChildRequirement = {
  childDescription: string | ReactNode
  childValidator: ValidatorFunction
}

type Requirement = {
  description: string | ReactNode
  validator: ValidatorFunction | ChildRequirement[]
  minPassedChildValidator?: number
}

interface RequirementChecklistPopoverProps {
  children: ReactNode
  id: string
  headerText: string
  fieldValue: string
  requirements: Requirement[]
}

export function RequirementChecklistPopover({ children, id, headerText, fieldValue, requirements }: RequirementChecklistPopoverProps) {
  return (
    <Popover
      placement="right"
      id={id}
      content={
        <div>
          <p className="mb-2">{headerText}</p>

          <ul className="max-w-[320px]">
            {requirements.map((requirement, idx) => {
              if (Array.isArray(requirement.validator)) {
                const { description, validator, minPassedChildValidator } = requirement
                const passedChildValidator = validator.filter(({ childValidator }) => childValidator(fieldValue)).length
                let isParentRequirementValid = passedChildValidator === validator.length

                if (minPassedChildValidator) {
                  isParentRequirementValid = passedChildValidator >= minPassedChildValidator
                }

                return (
                  <Fragment key={idx}>
                    <RequirementItem description={description} isValid={isParentRequirementValid} />

                    <ul className="pl-10">
                      {validator.map(({ childDescription, childValidator }, idx) => (
                        <RequirementItem key={idx} description={childDescription} isValid={childValidator(fieldValue)} />
                      ))}
                    </ul>
                  </Fragment>
                )
              }

              return <RequirementItem description={requirement.description} isValid={requirement.validator(fieldValue)} key={idx} />
            })}
          </ul>
        </div>
      }
      trigger={['focus', 'hover']}>
      {children}
    </Popover>
  )
}
